import { useEffect, useState } from 'react';

/**
 * Measures the given element's rendered height via ResizeObserver, so layout
 * that must clear a variable-height band (the sticky timed-course banner,
 * whose height depends on its content and the viewport width) can offset by a
 * measured value rather than an assumed one.
 *
 * Takes the element rather than a `RefObject` because the observed node
 * usually mounts conditionally: a callback ref kept in state re-runs the
 * measurement when the node appears or disappears, which a stable ref object
 * would not. Returns 0 whenever there is nothing to measure.
 *
 * jsdom (tests) and any non-browser host lack ResizeObserver; the single
 * synchronous measurement keeps behavior deterministic without it.
 */
export function useMeasuredHeight(element: HTMLElement | null): number {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!element) {
      setHeight(0);
      return undefined;
    }

    const measure = (): void => setHeight(element.getBoundingClientRect().height);
    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return height;
}
